/**
 * Preferências do painel: valem as salvas no servidor (por usuário); o localStorage
 * guarda só tema e densidade para o index.html aplicar antes da primeira pintura.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Density, Me, Preferences, TablePrefs, ThemePref } from '@berytus/contracts';
import { api } from './api.ts';
import { useMe } from './components/AuthGate.tsx';

export const DEFAULTS: Required<Pick<Preferences, 'theme' | 'density'>> = {
  theme: 'system',
  density: 'comfortable',
};

const LOCAL_KEY = 'berytus:ui';

function writeLocal(p: { theme: ThemePref; density: Density }) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(p));
  } catch {
    /* navegação privada: tudo bem, o servidor guarda */
  }
}

const darkQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
const subscribeScheme = (cb: () => void) => {
  darkQuery?.addEventListener('change', cb);
  return () => darkQuery?.removeEventListener('change', cb);
};
/** true quando o sistema operacional está em modo escuro (atualiza ao vivo). */
export const useSystemDark = () => useSyncExternalStore(subscribeScheme, () => !!darkQuery?.matches, () => false);

const narrowQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 1439px)') : null;
const subscribeNarrow = (cb: () => void) => {
  narrowQuery?.addEventListener('change', cb);
  return () => narrowQuery?.removeEventListener('change', cb);
};
/** true em telas de notebook (até 1439 px): o menu lateral começa recolhido para dar espaço às tabelas. */
export const useNarrowScreen = () => useSyncExternalStore(subscribeNarrow, () => !!narrowQuery?.matches, () => false);

export function resolveTheme(pref: ThemePref, systemDark: boolean): 'light' | 'dark' {
  return pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
}

export function usePreferences() {
  const me = useMe();
  const qc = useQueryClient();
  const prefs = { ...DEFAULTS, ...me.preferences };

  const update = useCallback(
    (patch: Preferences) => {
      // Otimista: a tela muda na hora; o servidor confirma em segundo plano.
      qc.setQueryData<Me>(['me'], (old) =>
        old
          ? {
              ...old,
              preferences: {
                ...old.preferences,
                ...patch,
                ...(patch.tables ? { tables: { ...old.preferences.tables, ...patch.tables } } : {}),
              },
            }
          : old,
      );
      api('/me/preferences', { method: 'PATCH', json: patch }).catch(() => qc.invalidateQueries({ queryKey: ['me'] }));
    },
    [qc],
  );

  const table = (id: string): TablePrefs => prefs.tables?.[id] ?? {};
  const updateTable = (id: string, patch: TablePrefs) => update({ tables: { [id]: { ...table(id), ...patch } } });

  return { prefs, update, table, updateTable };
}

/** Aplica tema e densidade no <html> e mantém o localStorage em dia. */
export function useApplyPreferences() {
  const { prefs } = usePreferences();
  const systemDark = useSystemDark();
  const theme = resolveTheme(prefs.theme, systemDark);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.dataset.density = prefs.density;
    writeLocal({ theme: prefs.theme, density: prefs.density });
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#0a0e1f');
  }, [theme, prefs.theme, prefs.density]);
}
